<template>
  <div class="newsletter-unsubscribe">
    <template v-if="!error">
      <fn-section
        constrained="l"
        background-color="sand-light"
        padding-top="xs"
        padding-bottom="xs"
        class="show-m"
      >
        <fn-breadcrumbs :nodes="page.content.breadcrumbs" />
      </fn-section>

      <fn-section
        class="nu-section"
        constrained="l"
        padding-top="xs"
        padding-bottom="m"
      >
        <div
          v-if="!showConfirm"
          class="nu-content"
        >
          <fn-grid>
            <fn-grid-item columns="12">
              <div class="nu-content-image">
                <fn-picture
                  :src="fullWidthImage.src"
                  :width="fullWidthImage.width"
                  :height="fullWidthImage.height"
                />
              </div>
            </fn-grid-item>
            <fn-grid-item
              class="nus-content"
              columns="12"
              columns-m="6"
              push-m="3"
            >
              <fn-headline
                type="h1"
                size="h2"
              >
                {{ $t('newsletter.unsubscribe.headline') }}
              </fn-headline>

              <fn-paragraph>
                {{ $t(`newsletter.unsubscribe.text.${type}`) }}
              </fn-paragraph>

              <div
                v-if="email"
                class="nus-content-email"
              >
                <fn-headline
                  type="h2"
                  size="h4"
                >
                  {{ $t(`newsletter.unsubscribe.emailAdressText.${type}`) }}
                </fn-headline>

                <fn-list
                  background-color="sand-light"
                  unstyled
                  icons
                  colored
                >
                  <li>
                    <fn-icon
                      icon="email"
                      size="xs"
                    />
                    <span>
                      {{ email }}
                    </span>
                  </li>
                </fn-list>
              </div>

              <div class="nus-content-buttons">
                <fn-link
                  color="orientation-green"
                  large
                  no-arrow
                  underline
                  :to="$t('url.homepage')"
                >
                  {{ $t('newsletter.unsubscribe.disagree') }}
                </fn-link>
                <fn-button
                  class="cb-unsubscribe"
                  secondary-alt
                  @click="$emit('unsubscribe')"
                >
                  {{ $t(`newsletter.unsubscribe.agree.${type}`) }}
                </fn-button>
              </div>

              <div class="nus-content-advantages">
                <fn-paragraph class="intro">
                  {{ $t('newsletter.unsubscribe.advantages.intro') }}
                </fn-paragraph>

                <fn-list
                  icons
                  unstyled
                >
                  <li>
                    <fn-icon
                      icon="check-bold"
                      color="green-dark"
                    />
                    {{ $t('newsletter.unsubscribe.advantages.listItem1') }}
                  </li>
                  <li>
                    <fn-icon
                      icon="check-bold"
                      color="green-dark"
                    />
                    {{ $t('newsletter.unsubscribe.advantages.listItem2') }}
                  </li>
                  <li>
                    <fn-icon
                      icon="check-bold"
                      color="green-dark"
                    />
                    {{ $t('newsletter.unsubscribe.advantages.listItem3') }}
                  </li>
                </fn-list>

                <fn-paragraph>
                  {{ $t('newsletter.unsubscribe.advantages.outro') }}
                </fn-paragraph>
              </div>
            </fn-grid-item>
          </fn-grid>
        </div>
        <fn-newsletter-unsubscribe-confirmation-section
          v-else
          :email="email"
          :image="image"
        />
      </fn-section>

      <fn-adobe-target-mbox id="newsletter_unreg_promotSlot_1" />
      <fn-adobe-target-mbox id="newsletter_unreg_promotSlot_2" />
      <fn-adobe-target-mbox id="newsletter_unreg_promotSlot_3" />

      <fn-cms-section-loader
        v-if="page.content.content_slots.a && page.content.content_slots.a.sections"
        :sections="page.content.content_slots.a.sections"
      />
    </template>
    <template v-else>
      <fn-error-newsletter-confirmation
        :image="image"
        unsubscribe
      />

      <fn-cms-section-loader
        v-if="page.content.content_slots.a && page.content.content_slots.a.sections"
        :sections="page.content.content_slots.a.sections"
      />
    </template>
  </div>
</template>

<script>
import ErrorImage from '@/assets/img/error-img.jpg';
/* eslint-disable import/no-duplicates */
import FullWidthImage from '@/assets/img/newsletter_unsubscribe.jpg';
import FullWidthImageSize from '@/assets/img/newsletter_unsubscribe.jpg?imageSize';
/* eslint-enable import/no-duplicates */

export default defineComponent({
  props: {
    page: {
      type: Object,
      required: true,
    },
    email: {
      type: String,
      default: '',
    },
    isCampaign: {
      type: Boolean,
      default: false,
    },
    showConfirm: {
      type: Boolean,
    },
    error: {
      type: Boolean,
    },
  },
  computed: {
    type() {
      return this.isCampaign ? 'campaign' : 'newsletter';
    },

    image() {
      return ErrorImage;
    },

    fullWidthImage() {
      return {
        src: FullWidthImage,
        ...FullWidthImageSize,
      };
    },
  },
});
</script>

<style lang="scss" scoped>
.newsletter-unsubscribe {
  .nu-section {
    padding: 0;
    background-color: transparent;

    @include bp(m) {
      padding: 0 10px 64px 10px;
      background-color: palette(sand, light);
    }
  }
  .nu-content {
    padding-bottom: 50px;
    overflow: hidden;
    background: $color-general-background-neutral-default;

    @include bp(m) {
      border-radius: map-get($border-radiuses, 'l');
    }
  }

  .nu-content-image {
    position: relative;
    display: block;
    overflow: hidden;
    max-height: 172px;

    @include bp(m) {
      max-height: 282px;
      border-radius: map-get($border-radiuses, 'l');
    }

    &:after {
      content: '';
      position: absolute;
      z-index: 1;
      bottom: 0;
      display: block;
      height: 80px;
      width: 100%;
      background: transparent;
      background: linear-gradient(90deg, $color-general-background-neutral-default 0%, transparent 100%);
      background: -moz-linear-gradient(90deg, $color-general-background-neutral-default 0%, transparent 100%);
      background: -webkit-linear-gradient(90deg, $color-general-background-neutral-default 0%, transparent 100%);
      filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#{$color-general-background-neutral-default}",
          endColorstr="transparent", GradientType=0);
    }

    .nu-headline {
      position: absolute;
      top: 120px;
      z-index: 1;
      margin-left: 120px;
      color: $color-general-text-inverse-default;
      font-size: 70px;
    }
  }

  .nus-content {
    padding: 0 10px;

    @include bp(m) {
      padding: 0;
    }

    .nus-content-advantages {
      margin-bottom: $pad;

      @include bp(m) {
        margin-bottom: 0;
      }
      .intro {
        margin-bottom: math.div($pad, 2);
      }

      .list {
        margin-bottom: 0;
        line-height: 20px;

        @include bp(m) {
          margin-bottom: math.div($pad, 2);
          line-height: 30px;
        }

        li {
          padding-left: $space;
          margin-bottom: math.div($pad, 2);

          @include bp(m) {
            margin-bottom: 0;
          }
        }
      }
    }

    .nus-content-buttons {
      display: flex;
      flex-direction: column-reverse;
      align-items: center;
      margin: $space 0;
      row-gap: $space;
      @include bp(m) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
      }

      .cb-unsubscribe {
        width: 100%;

        @include bp(m) {
          width: auto;
        }
      }
    }

    .nus-content-email {
      margin-top: 35px;

      .list {
        margin-bottom: 30px;
        border: 1px solid palette(grey, light);
      }
    }
  }
}
</style>
